import React, { useState, useEffect, useRef } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress
} from "@mui/material";
import { Visibility, VisibilityOff, PersonAddAlt1, Search as SearchIcon } from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { getCountryCodes } from "Utils/country";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import { API_ENDPOINTS } from "Constants";

//Services
import demoaccountapi from "Services/Demoaccount";

const PassengerCreate = (props) => {
  const { openDialog, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [countryCodeData, setCountryCodeData] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countrySearch, setCountrySearch] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const autocompleteService = useRef(null);

  const initAutocomplete = () => {
    if (autocompleteService.current) return;
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      return;
    }

    if (!document.getElementById("google-maps-places-script")) {
      const script = document.createElement("script");
      script.id = "google-maps-places-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_ENDPOINTS.GMAP_API}&libraries=places`;
      script.async = true;
      script.onload = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
      };
      document.head.appendChild(script);
    }
  };

  useEffect(() => {
    if (openDialog) {
      initAutocomplete();
    }
  }, [openDialog]);

  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        const countries = getCountryCodes();
        setCountryCodeData(countries);
        setLoadingCountries(false);
      } catch (error) {
        console.error('Error fetching country codes:', error);
        setLoadingCountries(false);
      }
    };

    fetchCountryCodes();
  }, []);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    formik.handleChange(e);

    if (!autocompleteService.current) {
      initAutocomplete();
    }

    if (value.trim().length >= 1) {
      const fetchPredictions = () => {
        if (autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(
            { input: value },
            (predictions, status) => {
              const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK || "OK";
              if (status === OK && predictions) {
                setAddressSuggestions(predictions);
              } else {
                setAddressSuggestions([]);
              }
            }
          );
        } else {
          setTimeout(() => {
            if (autocompleteService.current) {
              autocompleteService.current.getPlacePredictions(
                { input: value },
                (predictions, status) => {
                  const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK || "OK";
                  if (status === OK && predictions) {
                    setAddressSuggestions(predictions);
                  } else {
                    setAddressSuggestions([]);
                  }
                }
              );
            }
          }, 500);
        }
      };
      fetchPredictions();
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleAddressSelect = (description) => {
    formik.setFieldValue("Address", description);
    setAddressSuggestions([]);
  };

  const handleAddPassengers = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = {
      firstName: formValues?.firstName,
      country: formValues?.country,
      countryCode: formValues?.countryCode,
      mobileNumber: formValues?.mobileNumber,
      email: formValues?.email,
      password: formValues?.password,
      passConfirmation: formValues?.passConfirmation,
      SellerEmail: formValues?.SellerEmail,
      Address: formValues?.Address,
      TimeZone: formValues?.TimeZone,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "DemoAccount Created Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        setAddressSuggestions([]);
        handleClose();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while create demo account",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    demoaccountapi.addDemoaccount({ ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      email: "",
      password: "",
      passConfirmation: "",
      country: "",
      mobileNumber: "",
      countryCode: "",
      SellerEmail: "",
      Address: "",
      TimeZone: ""
    },
    validationSchema: yup.object({
      firstName: yup.string().required("Full Name is required").nullable(),
      email: yup
        .string()
        .email("Invalid email format")
        .required("Email is required")
        .nullable(),
      countryCode: yup.string().required("Country Code is required").nullable(),
      mobileNumber: yup
        .string()
          .min(5, "Mobile number must be at least 5 digits")
  .max(10, "Mobile number must not exceed 10 digits")
        .matches(/^\d{5,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      password: yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required")
        .nullable(),
      passConfirmation: yup
        .string()
        .required("Confirm password is required")
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .nullable(),
      country: yup.string().required("Country is required").nullable(),
      Address: yup
        .string()
        .required("Address is required")
        .nullable(),
      SellerEmail: yup.string()
        .email("Invalid email format")
        .required("Seller Email is required")
        .nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleAddPassengers,
  });
console.log(formik);
console.log(countryCodeData);
  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        formik.resetForm();
        setAddressSuggestions([]);
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.headerContainer}>
        <Typography className={classes.dialogTitle}>Demo Account</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Demo Account</span>
        </Typography>
      </Box>

      <DialogContent style={{ padding: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Full Name */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Full Name</Typography>
              <TextField
                name="firstName"
                placeholder="e.g. Sarah Jenkins"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Email Address */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Email Address</Typography>
              <TextField
                name="email"
                placeholder="sarah.j@taxiflow.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password</Typography>
              <TextField
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Password Confirmation */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password Confirmation</Typography>
              <TextField
                name="passConfirmation"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formik.values.passConfirmation}
                onChange={formik.handleChange}
                error={formik.touched.passConfirmation && Boolean(formik.errors.passConfirmation)}
                helperText={formik.touched.passConfirmation && formik.errors.passConfirmation}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Seller Email Id */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Seller Email</Typography>
              <TextField
                name="SellerEmail"
                placeholder="seller@domain.com"
                value={formik.values.SellerEmail}
                onChange={formik.handleChange}
                error={formik.touched.SellerEmail && Boolean(formik.errors.SellerEmail)}
                helperText={formik.touched.SellerEmail && formik.errors.SellerEmail}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Address</Typography>
              <Box sx={{ position: "relative", width: "100%" }}>
                <TextField
                  name="Address"
                  placeholder="123 Main St"
                  value={formik.values.Address}
                  onChange={handleAddressChange}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    setTimeout(() => setAddressSuggestions([]), 200);
                  }}
                  error={formik.touched.Address && Boolean(formik.errors.Address)}
                  helperText={formik.touched.Address && formik.errors.Address}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.formInput}
                  autoComplete="off"
                />
                {addressSuggestions.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      zIndex: 1300,
                      backgroundColor: "#ffffff",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      maxHeight: "220px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    {addressSuggestions.map((suggestion) => (
                      <Box
                        key={suggestion.place_id}
                        sx={{
                          padding: "10px 14px",
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "#0f172a",
                          fontFamily: "'Inter', sans-serif",
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background-color 0.2s ease, color 0.2s ease",
                          "&:last-child": {
                            borderBottom: "none",
                          },
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                            color: "#2563eb",
                            fontWeight: "500",
                          },
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleAddressSelect(suggestion.description);
                        }}
                        onClick={() => handleAddressSelect(suggestion.description)}
                      >
                        {suggestion.description}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Country</Typography>
              <TextField
                select
                name="country"
                value={formik.values.country}
                onChange={(e) => {
                  formik.handleChange(e);
                  const selectedCountry = countryCodeData.find(c => c.name === e.target.value);
                  if (selectedCountry) {
                     const countryCode = selectedCountry.dial_code.split("-")[0];
                    formik.setFieldValue("countryCode", countryCode);
                  }
                }}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => value || <span style={{ color: "#94a3b8" }}>Select Country</span>,
                  MenuProps: {
                    autoFocus: false,
                    onClose: () => setCountrySearch(""),
                  }
                }}
              >
                <MenuItem value="" disabled sx={{ display: "none" }}>
                  Select Country
                </MenuItem>

                <div style={{ padding: "8px", position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
                  <TextField
                    autoFocus
                    size="small"
                    placeholder="Search country..."
                    fullWidth
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    onClick={(e) => {
                      // Prevent the Select dropdown from closing when clicking inside
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      // Prevent the Select dropdown from closing when typing space and bubbling
                      e.stopPropagation();
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                {countryCodeData
                  .filter((c) =>
                    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                    c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
                    c.dial_code.toLowerCase().includes(countrySearch.toLowerCase())
                  )
                  .map((c) => (
                    <MenuItem key={c.code} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Mobile Number</Typography>
              <Box className={classes.mobileInputWrapper}>
                {/* <TextField
                  select
                  name="countryCode"
                  value={formik.values.countryCode}
                 onChange={(e) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length <= 10) {
      formik.setFieldValue("mobileNumber", value);
    }
  }}
                  variant="outlined"
                  size="small"
                  style={{ width: "90px" }}
                  className={classes.formInput}
                >
                  {countryCodeData.map((c) => (
                    <MenuItem key={c.code} value={c.dial_code}>
                      {c.dial_code}
                    </MenuItem>
                  ))}
                </TextField> */}
                <TextField
                  name="mobileNumber"
                  placeholder="000-000-0000"
                  value={formik.values.mobileNumber}
                  onChange={formik.handleChange}
                  error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
                  helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.formInput}
                />
              </Box>
            </Grid>
          </Grid>

          <Box className={classes.dialogActions}>
            <Button
              className={classes.cancelButton}
              onClick={() => {
                formik.resetForm();
                handleClose();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
            >
              <PersonAddAlt1 style={{ fontSize: "18px" }} />
              Create Demoaccount
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PassengerCreate;
